import { EventEmitter, inject, Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { BehaviorSubject, firstValueFrom, Observable, shareReplay, take } from 'rxjs';
import { AuthorizationRepository } from 'src/app/infrastructure/repositories/authorization.repository';
import { StorageKeys } from 'src/app/model/enums/storage-keys';
import { AuthorizationResult, JwtPayload } from 'src/app/model/interfaces/authorization-result';
import { JWT } from 'src/app/model/interfaces/json-web-token';
import { AppConfigService } from '../app-config.service';
import { ContextService } from '../context.service';
import { PlayerService } from '../player.service';
import { StorageService } from '../storage.service';

@Injectable()
export class AuthorizationService
{
    private readonly authorizationRepository = inject(AuthorizationRepository);
    private readonly contextService = inject(ContextService);
    private readonly storageService = inject(StorageService);
    private readonly playerService = inject(PlayerService);
    private readonly appConfigService = inject(AppConfigService);

    public expirationNotification = new EventEmitter<number>();
    public authorizationNeededNotification = new EventEmitter<boolean>();

    private loginStateSubject = new BehaviorSubject<boolean | null>(null);
    private loginState$ = this.loginStateSubject.asObservable().pipe(shareReplay(1));
    private expirationHandle: any = null;
    private tokenExpirationHandle: any = null;
    private uiActivityTimeoutHandle: any = null;
    private hasBeenUIActiveSinceJWTRefresh = false;

    public constructor(){}

    public setLoginState(state: boolean): void {
      this.loginStateSubject.next(state);
    }

    private getLoginState(): boolean | null {
      return this.loginStateSubject.value;
    }

    public isStateLoginStateValid(): Observable<boolean | null> {
      return this.loginState$;
    }

    public async isAuthorized(): Promise<boolean> {
      const token = this.storageService.getItem<string>(StorageKeys.JWT);
      
      if (!token) {
        return false;
      }

      let decodedToken: Pick<JwtPayload, 'sub' | 'exp'>;

      try {
        const fullToken = jwt_decode<JwtPayload>(token);
        decodedToken = { 
          sub: fullToken.sub, 
          exp: fullToken.exp
        };
      } catch {
        return false;
      }

      if (!this.isValidUserId(decodedToken.sub) || !decodedToken.exp) {
        return false;
      }

      const systemTime = await firstValueFrom(this.contextService.getSystemTime());

      return decodedToken.exp > systemTime.getTime() / 1000;
    }

  /**
   * Validates that the user ID (sub claim from JWT) is a valid positive integer.
   * 
   * Uses Number.isSafeInteger to prevent issues with large numbers and floating numbers (comma).
   * @param sub - The subject claim from the JWT token containing the user ID
   * @returns true if sub exists and can be parsed as a safe positive integer, otherwise false.
   */
    private isValidUserId(sub?: string): boolean {
      if (!sub) { 
        return false;
      }

      const userId = Number(sub);
      return Number.isSafeInteger(userId) && userId > 0;
    }

    public async signOut(): Promise<void>
    {
        try {
         
            this.performLogoutCleanup();
        } catch (error) {
            console.warn('Logout API call failed, but performing local cleanup:', error);
            // Even if API logout fails, we should still clean up local state
            this.performLogoutCleanup();
        }
    }

    private performLogoutCleanup(): void
    {
        // clear check for user activity
        window.clearTimeout(this.uiActivityTimeoutHandle);
        // clear timeout for promting for keep sesssin/token alive
        window.clearTimeout(this.expirationHandle);
        // clear jwtTokenTimout
        window.clearTimeout(this.tokenExpirationHandle);
        
        this.storageService.removeItem(StorageKeys.JWT);
        this.storageService.removeItem(StorageKeys.PlayerData);
        this.storageService.removeItem(StorageKeys.LotteryClassId);
        this.storageService.removeItem(StorageKeys.ReceiptOrderSmt);
        this.storageService.removeItem(StorageKeys.GapFundingOrderId);
        this.storageService.removeItem(StorageKeys.ReceiptOrderSubscription);
    }

    public authorize(username: string, password: string): Observable<AuthorizationResult>
    {
        const authorizationStatus = this.authorizationRepository.authorize(username, password);
        return authorizationStatus;
    }

    public updateToken(inputJwt: JWT): void
    {
      const token = inputJwt.Token;
      this.storageService.setItem(StorageKeys.JWT, token);

      let tokenExpireMs: number;
      let decodedToken: Pick<JwtPayload, 'exp'>;

      try {
        const fullToken = jwt_decode<JwtPayload>(token);
        if(!fullToken.exp) {
          throw new Error('Token does not contain exp claim');
        }

        tokenExpireMs = fullToken.exp
      } catch (error) {
        console.error('Failed to decode JWT token during updateToken.', error);
        this.clearTimeouts();
        this.signOut();

        return;
      }

      this.contextService.getSystemTime().pipe(take(1)).subscribe(now =>
      {
          const nowMs = now.getTime();
          const expirationMs = tokenExpireMs * 1000;
          const timeUntilExpirationMs = expirationMs - nowMs;

          if (timeUntilExpirationMs <= 0) {
            this.clearTimeouts();
            this.signOut();
            return;
          }

          this.clearTimeouts();

          const EXP_NOTIFY_OFFSET_MS = 3 * 60 * 1000;
          const UI_REFRESH_OFFSET_MS = 4 * 60 * 1000;

          // Deduct 3 mins from expiration time to allow for login update.
          const timeoutMs = Math.max(0, timeUntilExpirationMs - EXP_NOTIFY_OFFSET_MS);
          this.expirationHandle = setTimeout(() =>
          {
              this.notifyExpiration(timeoutMs);
          }, timeoutMs);

          // Real expired
          const tokenExpirationMS = expirationMs - nowMs;
          this.tokenExpirationHandle = setTimeout(() =>
          {
              this.authorizationExpired();
          }, tokenExpirationMS);

          // Reset flag for user activity
          this.hasBeenUIActiveSinceJWTRefresh = false;
          // Deduct 4 mins from expiration time to refresh jwt-token based on user ui activity
          const tokenUiRefreshMS = Math.max(0, timeUntilExpirationMs - UI_REFRESH_OFFSET_MS);
          this.uiActivityTimeoutHandle = setTimeout(() =>
          {
              this.keepAliveDueToUIAcivity();
          }, tokenUiRefreshMS);
      });
    }

    private clearTimeouts(): void {
      window.clearTimeout(this.expirationHandle);
      window.clearTimeout(this.tokenExpirationHandle);
      window.clearTimeout(this.uiActivityTimeoutHandle);
    }

    private async keepAliveDueToUIAcivity(): Promise<void>
    {
        if (this.hasBeenUIActiveSinceJWTRefresh) {
          try{
            await firstValueFrom(this.playerService.authRefresh());
          } catch (error) {
            console.warn('Auth refresh failed:', error);
          }
        }
    }

    private notifyExpiration(timeToExpiration: number): void
    {
        this.expirationNotification.emit(timeToExpiration);
    }

    private authorizationExpired(): void
    {
        this.authorizationNeededNotification.emit(true);
    }

    public hasBeenUIActive(): void
    {
        this.hasBeenUIActiveSinceJWTRefresh = true;
    }

    /**
     * Builds a DSIdentity API URL with returnUrl query parameter.
     * @param endpoint The DSIdentity API endpoint (e.g., 'login', 'createaccount')
     * @param returnUrl The return URL to append as query parameter
     * @returns Complete DSIdentity URL with query parameter
     */
    public buildDsIdentityUrl(endpoint: string): string {
      const webApiDsIdentityUrl = this.appConfigService.config['webapibasepath'] + 'dsidentity/';
      const returnUrl = sessionStorage.getItem(StorageKeys.LoginReferrer) || '/min-side';

      const url = new URL(endpoint, webApiDsIdentityUrl);
      url.searchParams.set('returnUrl', returnUrl);
        
      return url.toString();
    }
}
